import { createApi } from '@reduxjs/toolkit/query/react';
import { PaginationRequestType } from '../../types/pagination';
import { getBaseQuery, handleSessionExpiredError } from '../../utils/auth';
import {
  GetOffersParams,
  GetPostulationParams,
  PostulationInformation,
} from './offer-detail/types';
import { OffersForm, RecruitmentCard } from './offer-list/types';
import { ReviewRequest } from './postulation-review/review/types';
import {
  PostulationApplicationStage,
  SavePostulationApplicationStageParams,
  SavePostulationParams,
} from './postulation-review/types';

// Define a service using a base URL and expected endpoints
export const recruitmentApi = createApi({
  reducerPath: 'recruitmentApi',
  baseQuery: getBaseQuery('/mantainer-portal/recruitment'),
  endpoints: (builder) => ({
    // Managment
    getPostulationManagmentData: builder.query<any, string | number>({
      query: (id) => `/management/base-information/${id}`,
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    getPostulationManagmentStages: builder.query<
      PostulationApplicationStage[],
      string | number
    >({
      query: (id) => `/management/${id}`,
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    savePostulationManagmentStage: builder.mutation<
      void,
      SavePostulationApplicationStageParams
    >({
      query: ({ id, payload }) => ({
        url: `/management/${id}`,
        method: 'POST',
        body: payload,
      }),
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    savePostulationManagment: builder.mutation<void, SavePostulationParams>({
      query: ({ id, payload }) => ({
        url: `/management/${id}`,
        method: 'PUT',
        body: payload,
      }),
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    // Offers
    getOffers: builder.query<
      PaginationRequestType<RecruitmentCard>,
      GetOffersParams
    >({
      query: (params) => ({
        url: '',
        params,
      }),
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    getOffersData: builder.query<OffersForm, void>({
      query: () => '/form',
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    getPostulation: builder.query<ReviewRequest, string | number>({
      query: (id) => `/review/${id}`,
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
    getPostulations: builder.query<
      PaginationRequestType<PostulationInformation>,
      GetPostulationParams
    >({
      query: ({
        offerId,
        page = 1,
        idFilter,
        itemsPerPage = 10,
        steps = null,
      }) => ({
        url: `/postulations/${offerId}`,
        params: {
          page,
          itemsPerPage,
          id: idFilter || undefined,
          steps: steps?.length ? steps : undefined,
        },
      }),
      transformErrorResponse: (response) => handleSessionExpiredError(response),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPostulationManagmentDataQuery,
  useGetPostulationManagmentStagesQuery,
  useSavePostulationManagmentStageMutation,
  useSavePostulationManagmentMutation,
  useGetOffersQuery,
  useGetOffersDataQuery,
  useGetPostulationQuery,
  useGetPostulationsQuery,
} = recruitmentApi;
